import {
  Button,
  ButtonVariants,
  HelpIcon,
  Icon,
  SimplePagination,
  Spinner,
  Table,
} from '@dixa/lib-shared-habla';
import { AsyncStatus, DashboardQueuesType } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import NO_RESULT_IMAGE from 'images/no-search-results.png';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useServerStorage } from 'utils/useServerStorage';

import { QueuesItem } from './QueuesItem/QueuesItem';
import styles from './queuesOverview.module.scss';
import { VISIBLE_QUEUES_OVERVIEW_COLUMNS } from './shared';

type QueuesOverviewProps = {
  onQueuesTypeChange: (type: DashboardQueuesType) => void;
};

const MESSAGE_BUTTON_VARIANT: ButtonVariants = 'primary';

const Message = (props: {
  text: string;
  before?: JSX.Element | false;
  after?: JSX.Element | false;
}) => {
  return (
    <div className={styles.messageContainer}>
      {props.before && (
        <div className={styles.messageBefore}>{props.before}</div>
      )}

      <div className={styles.messageText}>{props.text}</div>

      {props.after && <div className={styles.messageAfter}>{props.after}</div>}
    </div>
  );
};

const TABLE_HEAD = (
  <Table.Thead>
    <Table.Tr>
      {VISIBLE_QUEUES_OVERVIEW_COLUMNS.map((column) => {
        const { label, icon, help, type, style } = column;

        return (
          <Table.Th key={type} style={style} className={styles[type]}>
            <div className={styles.tableHeadLabelContainer}>
              {icon ? <Icon name={icon} /> : null}
              <div className={styles.tableHeadLabel}>{label}</div>
              {help?.headline && help?.description && (
                <HelpIcon
                  className={styles.tableHeadIcon}
                  side="bottom"
                  title={help.headline}
                  text={help.description}
                />
              )}
            </div>
          </Table.Th>
        );
      })}
    </Table.Tr>
  </Table.Thead>
);

const LOADING_MESSAGE = (
  <Message text="Getting queues ..." before={<Spinner size={32} />} />
);

const MESSAGE_NO_RESULT_IMAGE = (
  <img
    className={styles.messageImage}
    src={NO_RESULT_IMAGE}
    alt="No result image"
  />
);

const ERROR_MESSAGE = <Message text="Error" />;

export const QueuesOverview = (props: QueuesOverviewProps): JSX.Element => {
  const navigate = useNavigate();
  const dashboardState = useAppSelector((state) => state.dashboard);
  const { data, set } = useServerStorage('user');

  const favoriteQueueIds = useMemo(
    () => data.dashboard?.favoriteQueueIds ?? [],
    [data?.dashboard],
  );

  const toggleFavoriteQueue = (queueId: string) => {
    set({
      dashboard: (draft) => {
        draft.favoriteQueueIds = draft.favoriteQueueIds ?? [];
        if (draft.favoriteQueueIds.includes(queueId)) {
          draft.favoriteQueueIds = draft.favoriteQueueIds.filter(
            (id) => id !== queueId,
          );
          ampli.dashboardQueueItemFavoriteToggled({ value: false });
        } else {
          draft.favoriteQueueIds.push(queueId);
          ampli.dashboardQueueItemFavoriteToggled({ value: true });
        }
      },
    });
  };

  const goToNextPage = (): void => {
    const {
      meta: { nextPageParams },
    } = dashboardState;

    if (!nextPageParams) {
      return;
    }

    const searchParams = new URLSearchParams(
      nextPageParams as unknown as Record<string, string>,
    );

    navigate(`?${searchParams}`);

    ampli.dashboardNextPageClicked();
  };

  const goToPrevPage = (): void => {
    const {
      meta: { prevPageParams },
    } = dashboardState;

    if (!prevPageParams) {
      return;
    }

    const searchParams = new URLSearchParams(
      prevPageParams as unknown as Record<string, string>,
    );

    navigate(`?${searchParams}`);

    ampli.dashboardPreviousPageClicked();
  };

  const renderQueues = () => {
    const { meta, queues } = dashboardState;
    const { onQueuesTypeChange } = props;

    const {
      ajaxAsyncStatus,
      nextPageParams,
      prevPageParams,
      currentPageParams: { type },
    } = meta;

    if (ajaxAsyncStatus === 'loading') {
      return LOADING_MESSAGE;
    }

    if (ajaxAsyncStatus === 'failed') {
      return ERROR_MESSAGE;
    }

    const renderedQueues = queues.map((queue) => {
      const { queue_id } = queue;

      return (
        <QueuesItem
          key={queue_id}
          dashboardQueue={queue}
          isFavourite={favoriteQueueIds.includes(queue_id)}
          toggleFavouriteStatus={() => toggleFavoriteQueue(queue_id)}
        />
      );
    });

    if (!renderedQueues.length && !(nextPageParams || prevPageParams)) {
      if (type === DashboardQueuesType.Favourite) {
        return (
          <Message
            text="There are no favorite queues."
            before={MESSAGE_NO_RESULT_IMAGE}
            after={
              <Button
                className={styles.messageButton}
                variant={MESSAGE_BUTTON_VARIANT}
                onClick={() => {
                  const type = DashboardQueuesType.Agent;
                  onQueuesTypeChange(type);
                  ampli.dashboardQueuesTypeChanged({
                    type,
                    location: 'favoritesMessageButton',
                  });
                }}
              >
                See my queues
              </Button>
            }
          />
        );
      }

      if (type === DashboardQueuesType.Agent) {
        return (
          <Message
            text="You are not assigned to any queues."
            before={MESSAGE_NO_RESULT_IMAGE}
            after={
              <Button
                className={styles.messageButton}
                variant={MESSAGE_BUTTON_VARIANT}
                onClick={() => {
                  const type = DashboardQueuesType.All;
                  onQueuesTypeChange(type);
                  ampli.dashboardQueuesTypeChanged({
                    type,
                    location: 'queuesMessageButton',
                  });
                }}
              >
                See all queues
              </Button>
            }
          />
        );
      }

      if (type === DashboardQueuesType.Archived) {
        return (
          <Message
            text="There are no archived queues."
            before={MESSAGE_NO_RESULT_IMAGE}
            after={
              <Button
                className={styles.messageButton}
                variant={MESSAGE_BUTTON_VARIANT}
                onClick={() => {
                  const type = DashboardQueuesType.All;
                  onQueuesTypeChange(type);
                  ampli.dashboardQueuesTypeChanged({
                    type,
                    location: 'archivedQueuesMessageButton',
                  });
                }}
              >
                See all queues
              </Button>
            }
          />
        );
      }
    }

    const tableFoot = (!!nextPageParams || !!prevPageParams) && (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={VISIBLE_QUEUES_OVERVIEW_COLUMNS.length}>
            <SimplePagination
              previousButtonClick={goToPrevPage}
              nextButtonClick={goToNextPage}
              previousButtonDisabled={!prevPageParams}
              nextButtonDisabled={!nextPageParams}
              loading={(ajaxAsyncStatus as AsyncStatus) === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );

    return (
      <Table
        stickyHeader
        stickyFooter
        headerWithBackground={false}
        framed={false}
      >
        {TABLE_HEAD}

        <Table.Tbody>{renderedQueues}</Table.Tbody>

        {tableFoot}
      </Table>
    );
  };

  return renderQueues();
};
